// Pregnancy Support Content - Hand-written tips and guidance
// Comprehensive week-by-week content for pregnant users
// No AI - all content carefully crafted
//
// MEDICAL REFERENCES & SOURCES:
// All information is based on peer-reviewed medical sources and official guidelines.
//
// Primary Sources:
// - American College of Obstetricians and Gynecologists (ACOG) - www.acog.org
// - Mayo Clinic - www.mayoclinic.org/healthy-lifestyle/pregnancy-week-by-week
// - American Pregnancy Association - www.americanpregnancy.org
// - March of Dimes - www.marchofdimes.org
// - Cleveland Clinic - www.my.clevelandclinic.org
// - National Institutes of Health (NIH) - www.nichd.nih.gov
// - World Health Organization (WHO) Pregnancy Guidelines
//
// Key Medical Guidelines Referenced:
// - ACOG Practice Bulletin #700: "Methods for Estimating the Due Date"
// - ACOG Committee Opinion #611: "Method for Estimating Due Date"
// - ACOG Guidelines on Prenatal Care (2023)
// - AAP/ACOG Guidelines for Perinatal Care, 8th Edition
//
// Specific Citations:
// - Morning sickness affects 70-80% of pregnancies (ACOG, 2023)
// - Heartbeat detection at 6-8 weeks, miscarriage risk drops after (Mayo Clinic)
// - Fetal development milestones (NIH Eunice Kennedy Shriver National Institute)
// - Viability at 24 weeks (ACOG Committee Opinion #579)
// - 5-1-1 Rule for labor timing (ACOG Patient Education)
// - Glucose screening 24-28 weeks (ACOG Practice Bulletin #190)
// - Weekly appointments after 36 weeks (ACOG Prenatal Care Guidelines)
// - Term classifications: Early Term 37-38, Full Term 39-40, Late Term 41 (ACOG/SMFM)
//
// Emotional/Psychological Content Sources:
// - Postpartum Support International - www.postpartum.net
// - ACOG Committee Opinion on Screening for Perinatal Depression
// - "Expecting Better" by Emily Oster (Evidence-based pregnancy guide)
// - "The Pregnancy Companion" (ACOG patient resource)
//
// Disclaimer: This content is for informational purposes only and does not
// constitute medical advice. Always consult your healthcare provider for
// personalized medical guidance.

export interface PregnancyValidation {
    situation: string;
    validation: string[];
    practicalTips: string[];
    selfCare: string;
}

export interface WeeklyPregnancyTip {
    week: number;
    title: string;
    bodyUpdate: string;
    babyUpdate: string;
    commonSymptoms: string[];
    selfCareTip: string;
    affirmation: string;
    askForThis: string;  // What to ask partner/support for
    doToday: string[];   // Practical actions
}

export interface DayOfWeekContent {
    day: number; // 0 = Sunday, 6 = Saturday
    theme: string;
    partnerFocus: string;
    pregnancyFocus: string;
}

// Validation scripts for common pregnancy concerns
export const PREGNANCY_VALIDATIONS: PregnancyValidation[] = [
    {
        situation: "I'm so exhausted all the time",
        validation: [
            "Fatigue in pregnancy is REAL. Your body is building an entire human.",
            "First trimester exhaustion is caused by rapidly rising hormones - it's not laziness.",
            "Many women need 10-12 hours of sleep in early pregnancy. You're not being dramatic."
        ],
        practicalTips: [
            "Nap when you can, even 20 minutes helps",
            "Go to bed earlier - don't push through",
            "Ask for help with evening chores"
        ],
        selfCare: "Give yourself permission to rest. Growing a baby is a full-time job your body is doing 24/7."
    },
    {
        situation: "Is this nausea normal?",
        validation: [
            "Morning sickness affects up to 80% of pregnant women - you're not alone.",
            "It can happen any time of day, not just morning. The name is misleading.",
            "Some days are worse than others. That's completely normal."
        ],
        practicalTips: [
            "Eat small amounts frequently - empty stomach makes it worse",
            "Keep crackers by your bed for before you get up",
            "Ginger and lemon can help - try ginger ale or tea"
        ],
        selfCare: "This phase usually eases by week 12-14. You're getting through it, one day at a time."
    },
    {
        situation: "I'm scared of miscarriage",
        validation: [
            "This fear is extremely common and doesn't mean anything bad will happen.",
            "After seeing a heartbeat at 6-8 weeks, miscarriage risk drops significantly.",
            "Your anxiety shows how much you care about this pregnancy."
        ],
        practicalTips: [
            "Talk to your provider about your concerns - they understand",
            "Avoid googling symptoms - it often increases anxiety",
            "Focus on what you can control: rest, nutrition, prenatal vitamins"
        ],
        selfCare: "It's okay to feel scared. Take it one day at a time. Most pregnancies result in healthy babies."
    },
    {
        situation: "I'm having mood swings",
        validation: [
            "Hormonal changes in pregnancy affect brain chemistry - this is biological, not weakness.",
            "Crying over commercials, getting angry quickly, feeling overwhelmed - all normal.",
            "Your partner should understand this isn't about them."
        ],
        practicalTips: [
            "Name your feelings when you can: 'I feel overwhelmed right now'",
            "Step away when you need to - it's okay to take space",
            "Light exercise can help stabilize mood"
        ],
        selfCare: "You're not 'crazy' - you're pregnant. Be gentle with yourself and ask for patience from others."
    },
    {
        situation: "I don't feel connected to the baby yet",
        validation: [
            "Not everyone feels instant bonding - and that's completely normal.",
            "For many women, connection grows gradually, especially after feeling movement.",
            "Feeling disconnected doesn't mean you won't be a good mom."
        ],
        practicalTips: [
            "Talk or sing to your belly - baby can hear you from week 18+",
            "Look at ultrasound pictures when you need a reminder",
            "Some people connect more after birth - that's okay too"
        ],
        selfCare: "Bonding is a process, not a switch. It will come in your own time and way."
    },
    {
        situation: "My body is changing so much",
        validation: [
            "Your body is doing exactly what it needs to do to grow a healthy baby.",
            "Weight gain in pregnancy is necessary and healthy - your body knows what it's doing.",
            "Every pregnant body looks different. Comparison isn't helpful."
        ],
        practicalTips: [
            "Invest in comfortable clothes that fit your new shape",
            "Take bump photos - you may treasure them later",
            "Focus on how you feel, not the number on the scale"
        ],
        selfCare: "This is temporary. Your body is incredible for what it's doing right now."
    },
    {
        situation: "I can't sleep at all",
        validation: [
            "Sleep disruption in pregnancy is extremely common, especially in third trimester.",
            "Between frequent urination, discomfort, and anxiety - quality sleep is hard.",
            "It's frustrating, and that frustration is valid."
        ],
        practicalTips: [
            "Use a pregnancy pillow for support",
            "Try sleeping slightly propped up if heartburn is an issue",
            "Nap during the day when possible - night sleep may not be enough"
        ],
        selfCare: "Do what you can, and ask for help during the day when you're exhausted."
    },
    {
        situation: "I'm scared about labor",
        validation: [
            "Fear of labor is one of the most common pregnancy anxieties - you're not alone.",
            "The unknown is always scary. Knowledge and preparation help.",
            "Your body is designed for this. Women have been doing it for thousands of years."
        ],
        practicalTips: [
            "Take a childbirth class - understanding the process reduces fear",
            "Discuss pain management options with your provider",
            "Create a birth plan but stay flexible - birth rarely follows a script"
        ],
        selfCare: "Focus on what you can control. Your medical team will guide you through it."
    },
    {
        situation: "Everyone keeps giving me advice",
        validation: [
            "Unsolicited advice is overwhelming, even when well-intentioned.",
            "You don't have to take anyone's advice. You're the parent.",
            "It's okay to set boundaries with family and friends."
        ],
        practicalTips: [
            "Have a go-to response: 'Thanks, I'll think about it'",
            "Ask your partner to help deflect unwanted advice",
            "Trust your instincts - you know what's right for you"
        ],
        selfCare: "You don't owe anyone explanations about your choices. This is your pregnancy."
    },
    {
        situation: "I'm not ready for this",
        validation: [
            "Feeling unprepared is completely normal - even for people who planned this.",
            "No one feels 100% ready. There's no 'ready enough' threshold.",
            "You'll learn as you go, just like every other parent."
        ],
        practicalTips: [
            "Focus on the essentials - baby needs less than you think at first",
            "Accept help when offered",
            "Trust that you'll figure things out as needed"
        ],
        selfCare: "You don't have to have it all figured out. You just have to show up with love."
    },
    {
        situation: "When should I go to the hospital?",
        validation: [
            "It's good that you're thinking about this - being prepared reduces panic.",
            "Most first-time labors are slow, so there's usually time.",
            "When in doubt, call your provider. That's what they're there for."
        ],
        practicalTips: [
            "Learn the 5-1-1 rule: contractions 5 min apart, 1 min long, for 1 hour",
            "Go immediately if: water breaks, bleeding, baby not moving, or severe pain",
            "Have your hospital bag packed and route planned by week 36"
        ],
        selfCare: "Trust your instincts. If something feels wrong, it's always okay to call or go in."
    },
    {
        situation: "I'm anxious about the anatomy scan",
        validation: [
            "The 20-week scan can bring up a lot of emotions - excitement and anxiety.",
            "This is when they check everything, so some nervousness is normal.",
            "Most scans show healthy babies. But it's okay to feel scared."
        ],
        practicalTips: [
            "Bring your partner for support",
            "Write down questions you want to ask beforehand",
            "It's okay to ask the technician to explain what they're seeing"
        ],
        selfCare: "Whatever the scan shows, you'll handle it. You're stronger than you know."
    },
    {
        situation: "I'm worried about being a good mom",
        validation: [
            "Every new parent worries about this. It shows you care deeply.",
            "There's no perfect way to parent. Love and trying your best is enough.",
            "You'll learn your baby and find your way together."
        ],
        practicalTips: [
            "Trust that instincts will kick in when baby arrives",
            "Accept that you'll make mistakes - everyone does",
            "Build a support network you can lean on"
        ],
        selfCare: "The fact that you're worried about being good means you already care. That's what matters most."
    },
    {
        situation: "I feel guilty about what I ate/did",
        validation: [
            "Pregnancy guilt is so common, but most things are fine in moderation.",
            "One cup of coffee, a bit of deli meat, a glass of wine before you knew - not catastrophic.",
            "Your baby is more resilient than the internet makes you believe."
        ],
        practicalTips: [
            "Talk to your provider about specific concerns",
            "Going forward, do your best - that's all anyone can do",
            "Avoid anxiety spirals by staying off Dr. Google"
        ],
        selfCare: "You're doing your best. That's enough. Let go of the guilt."
    },
    {
        situation: "I miss my old life",
        validation: [
            "Grieving your pre-baby identity and freedom is completely normal.",
            "You can love your baby AND miss spontaneity and independence.",
            "These feelings don't make you a bad mother."
        ],
        practicalTips: [
            "Do things that connect you to your pre-baby self while you can",
            "Plan date nights and friend time - relationships need nurturing",
            "Remember: life changes, but you'll find a new normal"
        ],
        selfCare: "It's okay to mourn the life you're leaving behind while also being excited for what's ahead."
    }
];

// Day-of-week themed content
export const DAY_OF_WEEK_CONTENT: DayOfWeekContent[] = [
    {
        day: 0, // Sunday
        theme: "Rest & Reset",
        partnerFocus: "Let her sleep in. Handle morning tasks. Suggest a relaxing activity together.",
        pregnancyFocus: "Take it slow today. Your body needs recovery time. Prioritize what matters."
    },
    {
        day: 1, // Monday
        theme: "Fresh Start",
        partnerFocus: "Start the week with encouragement. Ask how you can help with the week ahead.",
        pregnancyFocus: "One day at a time. You don't have to tackle everything today. Small progress counts."
    },
    {
        day: 2, // Tuesday
        theme: "Connection",
        partnerFocus: "Send a sweet midday text. Small gestures of affection matter right now.",
        pregnancyFocus: "Reach out to someone who understands. Connection helps on tough days."
    },
    {
        day: 3, // Wednesday
        theme: "Midweek Check-In",
        partnerFocus: "How is she really doing? Ask specifically and listen without fixing.",
        pregnancyFocus: "Halfway through the week. Check in with yourself: what do you need right now?"
    },
    {
        day: 4, // Thursday
        theme: "Preparation",
        partnerFocus: "Tackle one baby prep task together. Doing things side by side builds connection.",
        pregnancyFocus: "Do one small thing to prepare. Checking items off feels good."
    },
    {
        day: 5, // Friday
        theme: "Celebration",
        partnerFocus: "Plan something special for the weekend, even if it's just ordering her favorite food.",
        pregnancyFocus: "You made it through another week. Celebrate the small wins."
    },
    {
        day: 6, // Saturday
        theme: "Together Time",
        partnerFocus: "Be fully present today. Put phones away and spend real time together.",
        pregnancyFocus: "Do something that brings you joy. You deserve moments of happiness."
    }
];

// COMPREHENSIVE WEEKLY PREGNANCY TIPS - ALL 42 WEEKS
export const WEEKLY_PREGNANCY_TIPS: WeeklyPregnancyTip[] = [
    // Week 4
    {
        week: 4,
        title: "The Big News",
        bodyUpdate: "Your period is late. The test is positive. It might feel surreal - that's completely normal.",
        babyUpdate: "Baby is the size of a poppy seed. The neural tube is forming, which becomes the brain and spine.",
        commonSymptoms: ["Fatigue", "Breast tenderness", "Frequent urination", "Mild cramping"],
        selfCareTip: "Take a deep breath. This is big news. It's okay to feel excited, scared, or both.",
        affirmation: "I am capable of growing this baby. My body knows what to do.",
        askForThis: "I need you to be patient with me while I process this news.",
        doToday: ["Start prenatal vitamins with folic acid", "Make a first appointment (usually 8-10 weeks)", "Drink extra water"]
    },
    // Week 5
    {
        week: 5,
        title: "Early Days",
        bodyUpdate: "Hormones are surging. You may feel exhausted even though it's 'too early' to feel tired. It's not.",
        babyUpdate: "Baby is the size of a sesame seed. The heart is beginning to form and may start beating this week.",
        commonSymptoms: ["Extreme fatigue", "Nausea starting", "Sore breasts", "Mood swings"],
        selfCareTip: "Rest is not optional. Your body is working harder than you can see.",
        affirmation: "I am allowed to rest. Growing a baby is enough work for today.",
        askForThis: "I'm exhausted. Can you handle dinner tonight?",
        doToday: ["Nap without guilt", "Eat small frequent meals", "Go to bed early"]
    },
    // Week 6
    {
        week: 6,
        title: "Nausea Begins",
        bodyUpdate: "Morning sickness may hit hard now. Despite the name, it can last all day. This is temporary.",
        babyUpdate: "Baby is the size of a lentil. Tiny arm and leg buds are forming. The heart is beating!",
        commonSymptoms: ["Morning sickness", "Food aversions", "Heightened sense of smell", "Fatigue intensifying"],
        selfCareTip: "Eat whatever you can tolerate. Survival eating is okay right now.",
        affirmation: "This nausea means my hormones are strong. It will pass.",
        askForThis: "I can't handle cooking smells. Can you cook outside or bring something home?",
        doToday: ["Keep crackers by bed", "Try ginger tea or candies", "Eat before getting up"]
    },
    // Week 7
    {
        week: 7,
        title: "Surviving First Trimester",
        bodyUpdate: "The exhaustion and nausea are real. You're not being dramatic. Your body is in overdrive.",
        babyUpdate: "Baby is the size of a blueberry. Hands and feet are forming. Brain is developing rapidly.",
        commonSymptoms: ["Constant nausea", "Extreme fatigue", "Heightened emotions", "Food aversions"],
        selfCareTip: "Be gentle with yourself. First trimester is survival mode. That's okay.",
        affirmation: "I am doing enough by just getting through each day.",
        askForThis: "I need extra help around the house. I'm barely functioning.",
        doToday: ["Stay hydrated even if eating is hard", "Rest whenever possible", "Accept help"]
    },
    // Week 8
    {
        week: 8,
        title: "First Appointment",
        bodyUpdate: "This is often the week of your first prenatal visit. Exciting and nerve-wracking.",
        babyUpdate: "Baby is the size of a raspberry. All major organs are beginning to form. Tiny fingers are developing.",
        commonSymptoms: ["Morning sickness peaks", "Frequent urination", "Bloating", "Mood swings"],
        selfCareTip: "Write down your questions for the appointment. It helps when you're nervous.",
        affirmation: "I am taking care of myself and my baby. That's what matters.",
        askForThis: "Will you come to my appointment with me? I'd like the support.",
        doToday: ["Prepare appointment questions", "Eat what you can", "Rest before going"]
    },
    // Week 9
    {
        week: 9,
        title: "Emotional Roller Coaster",
        bodyUpdate: "Hormones may make you feel like crying one minute and laughing the next. This is normal.",
        babyUpdate: "Baby is the size of a grape. Heart is fully formed with four chambers. Looks more like a tiny human.",
        commonSymptoms: ["Mood swings", "Heightened emotions", "Continued nausea", "Headaches"],
        selfCareTip: "It's okay to cry. Your hormones are doing wild things. This will stabilize.",
        affirmation: "My emotions are valid. Pregnancy hormones are powerful.",
        askForThis: "I need patience right now. My emotions are all over the place.",
        doToday: ["Let yourself feel without judgment", "Talk about your feelings", "Take a walk if energy allows"]
    },
    // Week 10
    {
        week: 10,
        title: "Keeping the Secret",
        bodyUpdate: "Your pants may be tight but you're not quite showing. The 'secret phase' can feel isolating.",
        babyUpdate: "Baby is the size of a kumquat. Vital organs are functioning. Fingernails are forming.",
        commonSymptoms: ["Visible veins", "Nausea continuing", "Round ligament pain starting", "Thicker hair"],
        selfCareTip: "Find someone you can confide in. The secret can feel lonely.",
        affirmation: "I am not alone in this, even when I feel like I am.",
        askForThis: "I need to talk about this pregnancy with someone I trust.",
        doToday: ["Try on maternity clothes", "Confide in one trusted person", "Take belly photos"]
    },
    // Week 11
    {
        week: 11,
        title: "Light at the End",
        bodyUpdate: "Nausea may start easing for some. If not yet, hang in there - relief is coming.",
        babyUpdate: "Baby is the size of a fig. Now officially a fetus. Bones are hardening.",
        commonSymptoms: ["Nausea possibly easing", "Increased appetite", "Less fatigue", "Leg cramps"],
        selfCareTip: "If you're feeling better, don't overdo it. Ease back into activity slowly.",
        affirmation: "I am getting through this. The worst part may be behind me.",
        askForThis: "I'm feeling a bit better. Maybe we can take a short walk together?",
        doToday: ["Enjoy any improved energy", "Stay hydrated", "Gentle movement if possible"]
    },
    // Week 12
    {
        week: 12,
        title: "End of First Trimester",
        bodyUpdate: "Congratulations! You've made it through the hardest part. Miscarriage risk drops significantly.",
        babyUpdate: "Baby is the size of a plum. All organs are in place. Reflexes are developing.",
        commonSymptoms: ["Energy returning", "Nausea fading", "Slight bump forming", "Less frequent urination"],
        selfCareTip: "Celebrate this milestone! You did it. The first trimester is behind you.",
        affirmation: "I am strong. I made it through the most challenging weeks.",
        askForThis: "Can we celebrate making it to 12 weeks? This is a big deal.",
        doToday: ["Take a bump photo", "Consider announcing", "Treat yourself to something nice"]
    },
    // Week 13
    {
        week: 13,
        title: "Second Trimester Begins",
        bodyUpdate: "Welcome to the 'honeymoon trimester.' Energy often returns. Nausea usually fades.",
        babyUpdate: "Baby is the size of a peach. Fingerprints are forming. Vocal cords are developing.",
        commonSymptoms: ["Increased energy", "Reduced nausea", "Growing appetite", "Visible bump starting"],
        selfCareTip: "Enjoy this phase! Many women feel their best in the second trimester.",
        affirmation: "I am entering a new phase. I can enjoy this pregnancy.",
        askForThis: "I'm feeling better! Let's do something fun together.",
        doToday: ["Enjoy renewed energy", "Start light exercise if cleared", "Shop for maternity clothes"]
    },
    // Week 14
    {
        week: 14,
        title: "Growing Bump",
        bodyUpdate: "Your belly is starting to pop. Regular clothes are getting uncomfortable.",
        babyUpdate: "Baby is the size of a lemon. Can squint, frown, and grimace. Sucking thumb.",
        commonSymptoms: ["Visible bump", "Round ligament pain", "Increased energy", "Growing appetite"],
        selfCareTip: "Invest in comfortable maternity pants. You'll live in them.",
        affirmation: "My changing body is beautiful. I am creating life.",
        askForThis: "I need new clothes that fit. Can we go shopping together?",
        doToday: ["Buy maternity pants", "Take bump photos", "Moisturize your belly"]
    },
    // Week 15
    {
        week: 15,
        title: "Possible Movement",
        bodyUpdate: "Some women start feeling flutters this week. If not, don't worry - it's coming.",
        babyUpdate: "Baby is the size of an apple. Legs are longer than arms now. Can sense light.",
        commonSymptoms: ["Possible quickening (flutters)", "Stuffy nose", "Increased appetite", "Better sleep"],
        selfCareTip: "Pay attention to your belly. Those 'gas bubbles' might be baby!",
        affirmation: "Connection with my baby is growing stronger every day.",
        askForThis: "Put your hand on my belly. I think I might feel something.",
        doToday: ["Lie still and notice sensations", "Drink water", "Start baby registry research"]
    },
    // Week 16
    {
        week: 16,
        title: "Growing Connection",
        bodyUpdate: "You're undeniably pregnant now. The bump is visible. Strangers may notice.",
        babyUpdate: "Baby is the size of an avocado. Can make facial expressions. Grip reflex is strong.",
        commonSymptoms: ["Clear baby movement for some", "Round ligament pain", "Backaches beginning", "Glowing skin"],
        selfCareTip: "Talk to your baby. They can hear you now and will recognize your voice.",
        affirmation: "My baby knows my voice. We are already bonding.",
        askForThis: "Can you talk to the baby too? They can hear us now.",
        doToday: ["Talk or sing to baby", "Start a registry", "Take a belly photo"]
    },
    // Week 17
    {
        week: 17,
        title: "Aches and Changes",
        bodyUpdate: "Your body is adjusting to the extra weight. Backaches and round ligament pain are common.",
        babyUpdate: "Baby is the size of a pear. Fat is beginning to form. Skeleton is hardening.",
        commonSymptoms: ["Back pain", "Round ligament pain", "Leg cramps", "Stuffy nose"],
        selfCareTip: "Stretch gently. Consider prenatal yoga or swimming for relief.",
        affirmation: "My body is working hard. I will be gentle with myself.",
        askForThis: "My back hurts. Can you give me a massage?",
        doToday: ["Stretch gently", "Consider prenatal yoga", "Use a heating pad on low"]
    },
    // Week 18
    {
        week: 18,
        title: "Anatomy Scan Prep",
        bodyUpdate: "The big scan is coming up! Excitement and nervousness are both normal.",
        babyUpdate: "Baby is the size of a bell pepper. Ears are in position. Nervous system is developing.",
        commonSymptoms: ["Strong movements for many", "Dizziness", "Leg cramps at night", "Bigger appetite"],
        selfCareTip: "Write down questions for the anatomy scan. It helps to prepare.",
        affirmation: "I am ready to see my baby. Whatever happens, I can handle it.",
        askForThis: "I'm nervous about the scan. Will you come and hold my hand?",
        doToday: ["Schedule anatomy scan if not done", "Write down questions", "Drink lots of water"]
    },
    // Week 19
    {
        week: 19,
        title: "Almost Halfway",
        bodyUpdate: "You're almost at the midpoint! Your bump is undeniable now.",
        babyUpdate: "Baby is the size of a mango. A protective coating (vernix) covers their skin.",
        commonSymptoms: ["Hip pain", "Round ligament pain", "Shortness of breath starting", "Vivid dreams"],
        selfCareTip: "Rest when you need to. You're carrying more weight than you realize.",
        affirmation: "I am almost halfway there. I am doing amazing.",
        askForThis: "I need to rest my feet. Can you handle things for a bit?",
        doToday: ["Put your feet up", "Stay hydrated", "Consider a prenatal massage"]
    },
    // Week 20
    {
        week: 20,
        title: "Halfway Point!",
        bodyUpdate: "You've made it halfway! This is a huge milestone. Celebrate it.",
        babyUpdate: "Baby is the size of a banana. Can hear sounds outside the womb. Swallows amniotic fluid.",
        commonSymptoms: ["Strong movements", "Leg cramps", "Heartburn starting", "Increased vaginal discharge"],
        selfCareTip: "Take a halfway photo! Document this milestone moment.",
        affirmation: "I am halfway to meeting my baby. I can do the second half.",
        askForThis: "Let's celebrate being halfway! This is a big deal.",
        doToday: ["Take a milestone photo", "Celebrate somehow", "Write a letter to baby"]
    },
    // Week 21
    {
        week: 21,
        title: "Baby is Active",
        bodyUpdate: "You should be feeling regular movement now. It's reassuring and amazing.",
        babyUpdate: "Baby is the size of a carrot. Eyebrows and eyelids are fully formed. Very active!",
        commonSymptoms: ["Strong kicks", "Braxton Hicks starting", "Backaches", "Stretch marks appearing"],
        selfCareTip: "Enjoy the kicks! This is the fun part of pregnancy.",
        affirmation: "Every kick reminds me that my baby is thriving.",
        askForThis: "Feel my belly! The baby is kicking right now!",
        doToday: ["Count kicks after meals", "Moisturize stretch marks", "Enjoy the movement"]
    },
    // Week 22
    {
        week: 22,
        title: "Baby Can Hear You",
        bodyUpdate: "Baby's hearing is developing. They can hear your voice and heartbeat.",
        babyUpdate: "Baby is the size of a papaya. Has a strong grip. Taste buds are forming.",
        commonSymptoms: ["Increased appetite", "Swelling in feet", "Stretch marks", "Nasal congestion"],
        selfCareTip: "Read or sing to your baby. They're listening!",
        affirmation: "My voice is my baby's favorite sound.",
        askForThis: "Will you read to the baby with me tonight?",
        doToday: ["Sing or read to baby", "Elevate swollen feet", "Drink water to reduce swelling"]
    },
    // Week 23
    {
        week: 23,
        title: "Getting Bigger",
        bodyUpdate: "Your belly is growing fast. Finding comfortable positions gets harder.",
        babyUpdate: "Baby is the size of a grapefruit. Skin is developing pigment. Lungs are maturing.",
        commonSymptoms: ["Backaches", "Trouble sleeping", "Swelling", "Skin changes"],
        selfCareTip: "A pregnancy pillow can be life-changing. Consider investing in one.",
        affirmation: "I am adapting to my changing body. I am resilient.",
        askForThis: "I need a pregnancy pillow. My back is killing me.",
        doToday: ["Research pregnancy pillows", "Try new sleeping positions", "Gentle stretching"]
    },
    // Week 24
    {
        week: 24,
        title: "Viability Milestone",
        bodyUpdate: "Baby could survive outside the womb with intensive medical care now. A big milestone.",
        babyUpdate: "Baby is the size of an ear of corn. Lungs are producing surfactant. Regular sleep-wake cycles.",
        commonSymptoms: ["Braxton Hicks", "Stretch marks", "Linea nigra appearing", "Itchy skin"],
        selfCareTip: "Take a moment to acknowledge this milestone. Viability is meaningful.",
        affirmation: "My baby has reached viability. We are both strong.",
        askForThis: "This is a huge milestone. I need you to celebrate this with me.",
        doToday: ["Acknowledge viability milestone", "Moisturize itchy skin", "Take a belly photo"]
    },
    // Week 25
    {
        week: 25,
        title: "Glucose Test Week",
        bodyUpdate: "The glucose screening test happens around now. The drink is not delicious.",
        babyUpdate: "Baby is the size of a rutabaga. Hair is growing. Responds to your voice.",
        commonSymptoms: ["Heartburn", "Constipation", "Braxton Hicks", "Carpal tunnel symptoms"],
        selfCareTip: "The glucose drink is temporary. Bring a distraction for the waiting hour.",
        affirmation: "This test is just precautionary. I am taking care of myself.",
        askForThis: "Will you come with me to the glucose test? It takes an hour.",
        doToday: ["Schedule or complete glucose test", "Bring snacks for after", "Stay calm"]
    },
    // Week 26
    {
        week: 26,
        title: "Eyes Open",
        bodyUpdate: "You might feel short of breath as baby pushes on your diaphragm.",
        babyUpdate: "Baby is the size of a head of lettuce. Eyes can open now! May respond to light.",
        commonSymptoms: ["Shortness of breath", "Trouble sleeping", "Swelling", "Frequent urination returns"],
        selfCareTip: "Slow down when walking. You're carrying a lot of extra weight.",
        affirmation: "I am allowed to slow down. My body is doing incredible work.",
        askForThis: "I need to take breaks. Don't rush me.",
        doToday: ["Move slowly", "Elevate feet when sitting", "Practice deep breathing"]
    },
    // Week 27
    {
        week: 27,
        title: "End of Second Trimester",
        bodyUpdate: "The second trimester is ending. You're entering the home stretch!",
        babyUpdate: "Baby is the size of a cauliflower. Brain is very active. Has regular sleep cycles.",
        commonSymptoms: ["Leg cramps", "Hemorrhoids", "Swelling", "Braxton Hicks increasing"],
        selfCareTip: "Pat yourself on the back. Two trimesters down, one to go!",
        affirmation: "I am in the final stretch. I can do this.",
        askForThis: "Can we start planning for baby's arrival? We're in the home stretch.",
        doToday: ["Celebrate the milestone", "Start thinking about birth class", "Review registry"]
    },
    // Week 28
    {
        week: 28,
        title: "Third Trimester Begins",
        bodyUpdate: "Welcome to the third trimester! Fatigue may return. Sleep gets harder.",
        babyUpdate: "Baby is the size of an eggplant. Can blink and has eyelashes. Dreams during REM sleep.",
        commonSymptoms: ["Fatigue returning", "Shortness of breath", "Heartburn", "Trouble sleeping"],
        selfCareTip: "Appointments will increase to every 2 weeks now. You're being monitored closely.",
        affirmation: "I am on the home stretch. Meeting my baby is so close.",
        askForThis: "I'm getting tired again. I need extra help like in the first trimester.",
        doToday: ["Schedule more frequent appointments", "Consider birth classes", "Start hospital bag list"]
    },
    // Week 29
    {
        week: 29,
        title: "Getting Heavy",
        bodyUpdate: "Your belly is big and may be in the way. Simple tasks feel harder.",
        babyUpdate: "Baby is the size of a butternut squash. Muscles and lungs are maturing.",
        commonSymptoms: ["Back pain", "Difficulty breathing", "Frequent urination", "Leg cramps at night"],
        selfCareTip: "It's okay to ask for help with things you used to do easily.",
        affirmation: "Asking for help is not weakness. I am smart to conserve energy.",
        askForThis: "Can you help me with _____? It's hard to do with this belly.",
        doToday: ["Accept help without guilt", "Do gentle stretches", "Sleep with pillows between knees"]
    },
    // Week 30
    {
        week: 30,
        title: "10 Weeks to Go",
        bodyUpdate: "Single digits feel so close! But these last weeks can feel long.",
        babyUpdate: "Baby is the size of a cabbage. Brain surface is developing wrinkles (gyri). Growing fast!",
        commonSymptoms: ["Braxton Hicks increasing", "Pelvic pressure", "Heartburn", "Trouble sleeping"],
        selfCareTip: "Stay patient. These weeks matter for baby's development.",
        affirmation: "Every day brings me closer to meeting my baby.",
        askForThis: "I'm getting impatient. Can we do something to distract me?",
        doToday: ["Practice breathing exercises", "Start packing hospital bag", "Install car seat"]
    },
    // Week 31
    {
        week: 31,
        title: "Hospital Bag Time",
        bodyUpdate: "Baby is running out of room. You may feel every movement intensely.",
        babyUpdate: "Baby is the size of a coconut. All five senses are working. Processing information.",
        commonSymptoms: ["Intense movements", "Shortness of breath", "Leaky breasts (colostrum)", "Nesting urges"],
        selfCareTip: "Pack your hospital bag now. You'll feel more prepared.",
        affirmation: "I am preparing for baby's arrival. I am ready.",
        askForThis: "Help me pack the hospital bag. I want us both to be ready.",
        doToday: ["Pack hospital bag", "Tour the hospital if not done", "Wash baby clothes"]
    },
    // Week 32
    {
        week: 32,
        title: "Nesting Intensifies",
        bodyUpdate: "Nesting urges may be strong. You want everything perfect for baby.",
        babyUpdate: "Baby is the size of a squash. Practicing breathing. Fingernails are fully formed.",
        commonSymptoms: ["Strong nesting urge", "Braxton Hicks", "Fatigue", "Shortness of breath"],
        selfCareTip: "Listen to nesting urges, but don't exhaust yourself. Rest is important too.",
        affirmation: "I am creating a welcoming space for my baby.",
        askForThis: "Help me with the nursery. I want it ready but I'm getting tired.",
        doToday: ["One nesting project", "Rest afterwards", "Finish registering at hospital"]
    },
    // Week 33
    {
        week: 33,
        title: "Birth Plan",
        bodyUpdate: "It's a good time to finalize your birth plan. Stay flexible, but have preferences.",
        babyUpdate: "Baby is the size of a pineapple. Bones are hardening but skull stays soft for birth.",
        commonSymptoms: ["Pelvic pressure", "Difficulty walking", "Insomnia", "Heartburn"],
        selfCareTip: "A birth plan helps communicate your wishes, but birth rarely follows a script.",
        affirmation: "I am informed about my options. I will advocate for myself.",
        askForThis: "Let's talk through the birth plan together. Your support matters.",
        doToday: ["Write or finalize birth plan", "Discuss with provider", "Review with partner"]
    },
    // Week 34
    {
        week: 34,
        title: "Final Weeks",
        bodyUpdate: "Everything feels big and uncomfortable. But you're so close!",
        babyUpdate: "Baby is the size of a cantaloupe. Lungs are almost mature. Fat is accumulating.",
        commonSymptoms: ["Fatigue", "Swelling everywhere", "Difficulty breathing", "Frequent contractions"],
        selfCareTip: "Rest as much as possible. Your body needs to prepare for labor.",
        affirmation: "I am almost there. My body is preparing perfectly.",
        askForThis: "I need more help now. Please take over as much as you can.",
        doToday: ["Rest with feet up", "Freeze some meals", "Finalize work transition"]
    },
    // Week 35
    {
        week: 35,
        title: "Car Seat Check",
        bodyUpdate: "Baby may drop lower (lightning), making breathing easier but peeing harder.",
        babyUpdate: "Baby is the size of a honeydew. Kidneys are fully developed. Gaining weight fast.",
        commonSymptoms: ["Lightning crotch", "Frequent urination", "Pelvic pressure", "Braxton Hicks"],
        selfCareTip: "Make sure the car seat is installed correctly. Many fire stations will check for free.",
        affirmation: "I am handling the details. Everything is coming together.",
        askForThis: "Can you get the car seat installed and checked?",
        doToday: ["Install or check car seat", "Review hospital route", "Pack snacks for hospital bag"]
    },
    // Week 36
    {
        week: 36,
        title: "Almost Full Term",
        bodyUpdate: "Appointments are now weekly. Baby could come anytime in the next month.",
        babyUpdate: "Baby is the size of a romaine lettuce. Losing lanugo (fine hair). Almost ready!",
        commonSymptoms: ["Pelvic pressure intense", "Trouble sleeping", "Nesting overdrive", "Anxiety about labor"],
        selfCareTip: "Rest is your priority now. Everything else can wait or be delegated.",
        affirmation: "I have done everything I can to prepare. I am ready.",
        askForThis: "I'm anxious about labor. Can we talk through the plan again?",
        doToday: ["Review birth plan with partner", "Practice breathing/relaxation", "Rest"]
    },
    // Week 37
    {
        week: 37,
        title: "Early Term",
        bodyUpdate: "Baby is considered 'early term.' Labor could start anytime!",
        babyUpdate: "Baby is the size of a winter melon. Lungs are mature. Practicing breathing movements.",
        commonSymptoms: ["Bloody show possible", "Mucus plug may release", "Intense Braxton Hicks", "Nesting"],
        selfCareTip: "Know the signs of labor: regular contractions, water breaking, bloody show.",
        affirmation: "I am ready whenever baby decides to arrive.",
        askForThis: "Let's review when we should go to the hospital. I want us both to know.",
        doToday: ["Review labor signs", "Keep phone charged", "Hospital bag by door"]
    },
    // Week 38
    {
        week: 38,
        title: "Any Day Now",
        bodyUpdate: "You're full term! Baby is ready. Now you wait (the hardest part).",
        babyUpdate: "Baby is the size of a leek. Organs are ready for life outside. Just gaining weight now.",
        commonSymptoms: ["Extreme impatience", "Difficulty sleeping", "Contractions may start/stop", "Nesting"],
        selfCareTip: "Distract yourself with things you enjoy. Staring at the clock doesn't help.",
        affirmation: "My baby will come when they're ready. I can wait.",
        askForThis: "I'm going crazy waiting. Let's do something to distract me.",
        doToday: ["Watch a movie", "Go for a walk", "Do something you enjoy"]
    },
    // Week 39
    {
        week: 39,
        title: "Full Term",
        bodyUpdate: "You are officially full term. Baby could come any moment!",
        babyUpdate: "Baby is the size of a small pumpkin. Fully developed. Just waiting for the signal.",
        commonSymptoms: ["Prelabor symptoms", "Loose stools", "Lower back pain", "Restlessness"],
        selfCareTip: "Stay busy but take it easy. Rest when you can - you'll need energy for labor.",
        affirmation: "I am as ready as I can be. My body knows how to birth this baby.",
        askForThis: "Stay close this week. I want you nearby when it starts.",
        doToday: ["Rest", "Stay hydrated", "Finalize any last details"]
    },
    // Week 40
    {
        week: 40,
        title: "Due Date Week",
        bodyUpdate: "Your due date is here! But only 5% of babies arrive on the exact date.",
        babyUpdate: "Baby is the size of a small pumpkin. Completely ready. Just picking their birthday!",
        commonSymptoms: ["Everything is uncomfortable", "Impatience at max", "Possible labor signs", "Exhaustion"],
        selfCareTip: "Patience is hard now. But baby will come. Trust the process.",
        affirmation: "My due date is a guess. Baby will come when they're ready.",
        askForThis: "Please don't ask if baby is here yet. I'll tell you when it happens.",
        doToday: ["Rest", "Distract yourself", "Trust your body"]
    },
    // Week 41
    {
        week: 41,
        title: "Past Due Date",
        bodyUpdate: "You're past your due date. This is frustrating but not uncommon.",
        babyUpdate: "Baby is fully developed. Some babies just need a few extra days.",
        commonSymptoms: ["Maximum discomfort", "Frustration", "Anxiety", "Impatience"],
        selfCareTip: "Your provider will monitor you closely. Talk to them about induction options.",
        affirmation: "My baby will come. Some babies just need more time.",
        askForThis: "I'm frustrated and uncomfortable. I need extra support and patience.",
        doToday: ["Attend monitoring appointments", "Discuss induction timeline", "Try to stay positive"]
    },
    // Week 42
    {
        week: 42,
        title: "Final Stretch",
        bodyUpdate: "If you're still pregnant, you're likely being monitored closely and may be induced.",
        babyUpdate: "Baby is ready but comfortable! Induction ensures baby's safety.",
        commonSymptoms: ["Extreme discomfort", "Anxiety", "Frustration", "Ready to be done"],
        selfCareTip: "You've done everything right. Induction is just the next step in meeting your baby.",
        affirmation: "However baby arrives, I will meet my baby soon.",
        askForThis: "I need reassurance. Tell me we're almost there.",
        doToday: ["Follow medical guidance", "Stay calm", "Pack any last-minute items"]
    }
];

// Helper function to get pregnancy validation by keyword
export function getPregnancyValidation(query: string): PregnancyValidation | null {
    const lowerQuery = query.toLowerCase();
    return PREGNANCY_VALIDATIONS.find(v =>
        v.situation.toLowerCase().includes(lowerQuery.slice(0, 15)) ||
        lowerQuery.includes('exhaust') && v.situation.includes('exhausted') ||
        lowerQuery.includes('tired') && v.situation.includes('exhausted') ||
        lowerQuery.includes('nausea') && v.situation.includes('nausea') ||
        lowerQuery.includes('sick') && v.situation.includes('nausea') ||
        lowerQuery.includes('miscarriage') && v.situation.includes('miscarriage') ||
        lowerQuery.includes('scared') && (v.situation.includes('scared') || v.situation.includes('miscarriage')) ||
        lowerQuery.includes('mood') && v.situation.includes('mood') ||
        lowerQuery.includes('emotional') && v.situation.includes('mood') ||
        lowerQuery.includes('connect') && v.situation.includes('connected') ||
        lowerQuery.includes('body') && v.situation.includes('body') ||
        lowerQuery.includes('weight') && v.situation.includes('body') ||
        lowerQuery.includes('sleep') && v.situation.includes('sleep') ||
        lowerQuery.includes('labor') && v.situation.includes('labor') ||
        lowerQuery.includes('advice') && v.situation.includes('advice') ||
        lowerQuery.includes('ready') && v.situation.includes('ready') ||
        lowerQuery.includes('hospital') && v.situation.includes('hospital') ||
        lowerQuery.includes('scan') && v.situation.includes('scan') ||
        lowerQuery.includes('anatomy') && v.situation.includes('anatomy') ||
        lowerQuery.includes('mom') && v.situation.includes('good mom') ||
        lowerQuery.includes('guilty') && v.situation.includes('guilty') ||
        lowerQuery.includes('miss') && v.situation.includes('miss')
    ) || null;
}

// Helper function to get day content
export function getDayContent(date: Date = new Date()): DayOfWeekContent {
    return DAY_OF_WEEK_CONTENT[date.getDay()];
}

// Helper function to get pregnancy tip for week
export function getPregnancyTipForWeek(week: number): WeeklyPregnancyTip | null {
    // Find exact match first
    const exactMatch = WEEKLY_PREGNANCY_TIPS.find(t => t.week === week);
    if (exactMatch) return exactMatch;

    // Find the closest lower week
    const sortedTips = [...WEEKLY_PREGNANCY_TIPS].sort((a, b) => b.week - a.week);
    return sortedTips.find(t => t.week <= week) || WEEKLY_PREGNANCY_TIPS[0];
}
